// src/pages/auth/Otp.tsx
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiAlertCircle } from "react-icons/fi";
import OtpForm from "@/components/auth/components/OtpForm";
import { login } from "@/api/client";

const LOGO = "https://cdn.tranzytours.com/assets/branding/logo.png";

type OtpLocationState = {
  username?: string;
  password?: string;
};

function safeRedirect(raw: string | null, fallback: string) {
  const v = (raw || "").trim();
  if (!v) return fallback;

  // internal only
  if (!v.startsWith("/")) return fallback;
  if (v.startsWith("//")) return fallback;
  if (v.includes("://")) return fallback;

  return v;
}

export default function Otp() {
  const navigate = useNavigate();
  const location = useLocation();

  const { username, password } = (location.state as OtpLocationState) || {};

  // ✅ read redirect from URL: /otp?redirect=/book/xyz
  const redirectTo = useMemo(() => {
    const p = new URLSearchParams(location.search);
    return safeRedirect(p.get("redirect"), "/dashboard");
  }, [location.search]);

  // Whether the user arrived via an explicit deep link (?redirect=…). Used so
  // the portal chooser only honors a real target and otherwise routes by role.
  const hasExplicitRedirect = useMemo(
    () => !!new URLSearchParams(location.search).get("redirect"),
    [location.search]
  );

  const [error, setError] = useState<string | null>(null);

  // If user lands here without credentials (e.g. refresh / direct hit)
  // → send them back to signin WITH redirect preserved.
  useEffect(() => {
    if (!username || !password) {
      navigate(`/signin?redirect=${encodeURIComponent(redirectTo)}`, { replace: true });
    }
  }, [username, password, navigate, redirectTo]);

  const handleVerify = async (code: string) => {
    if (!username || !password) {
      setError("Your session expired. Please login again.");
      navigate(`/signin?redirect=${encodeURIComponent(redirectTo)}`, { replace: true });
      return;
    }

    setError(null);
    try {
      const result = await login({ username, password, otp: code } as any);

      if (result && result.step === "done") {
        // ✅ go where the user originally wanted to go
        navigate(redirectTo, { replace: true });
        return;
      }

      // Multi-role account → choose which portal to enter
      if (result && result.step === "select-role") {
        navigate("/select-portal", {
          replace: true,
          state: {
            selectionToken: result.selectionToken,
            availableRoles: result.availableRoles,
            redirectTo: hasExplicitRedirect ? redirectTo : undefined,
          },
        });
        return;
      }

      if (result && result.step === "otp") {
        setError("Please enter the latest code we sent to your email.");
        return;
      }

      setError("Something went wrong. Please try again.");
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Invalid or expired code. Please try again.";
      setError(msg);
    }
  };

  const handleResend = async () => {
    if (!username || !password) {
      setError("Your session expired. Please login again.");
      navigate(`/signin?redirect=${encodeURIComponent(redirectTo)}`, { replace: true });
      return;
    }

    setError(null);
    try {
      const result = await login({ username, password } as any);

      if (!result || result.step !== "otp") {
        setError("Could not resend code. Please try again.");
      }
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Could not resend code. Please try again.";
      setError(msg);
    }
  };

  return (
    <div className="min-h-screen bg-[#0ea5e9]/85 md:bg-[#0ea5e9] relative overflow-hidden">
      <div className="pointer-events-none absolute -left-10 -top-10 h-40 w-40 rounded-full bg-white/30 blur-md" />
      <div className="pointer-events-none absolute -right-12 bottom-8 h-44 w-44 rounded-full bg-white/30 blur-md" />

      <div className="mx-auto max-w-6xl px-4 py-8 md:py-14">
        <div className="relative mx-auto max-w-md rounded-3xl bg-white shadow-[0_25px_80px_rgba(2,6,23,0.25)] overflow-hidden border border-gray-200">
          <div className="px-8 py-10">
            <div className="mb-4 flex items-center justify-center">
              <img src={LOGO} alt="Tranzy Tours" className="h-12" />
            </div>

            <h1 className="text-center text-3xl font-bold text-sky-600">
              Verify Code
            </h1>
            <p className="mt-1 text-center text-xs text-gray-500">
              Enter the 6-digit code sent to your email
            </p>

            {error && (
              <div className="mt-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                <FiAlertCircle className="mt-0.5 shrink-0" />
                <div className="leading-5">{error}</div>
              </div>
            )}

            <OtpForm
              length={6}
              initialSeconds={30}
              onVerify={handleVerify}
              onResend={handleResend}
            />

            <div className="mt-4 text-center text-xs">
              <Link
                to={`/signin?redirect=${encodeURIComponent(redirectTo)}`}
                className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-800"
              >
                <FiArrowLeft /> Back to sign in
              </Link>
            </div>
          </div>

          <div className="h-2 w-full bg-gradient-to-r from-sky-400 via-sky-500 to-sky-600" />
        </div>
      </div>
    </div>
  );
}
